import subscriptionApi from "@/api/subscription.api";
import { notificationError } from "@/common/helpers/notifications.helper";
import { i18n } from "@/plugins/i18n";
import {
  getAnnualNudgeVariant,
  getPostHogDistinctId,
  getPricingExperimentVariant,
  PRICING_EXPERIMENT_NAME,
} from "@/services/PostHogService";

/**
 * Persist pricing experiment fields on Stripe checkout create so tools-service can
 * store them (metadata/DB) and attach them to paid_plan_created from the webhook.
 * Caller may set posthog_* fields explicitly (preferred when UI arm is known).
 */
const withPosthogExperimentProps = (payload = {}) => {
  const next = { ...payload };

  if (!Object.prototype.hasOwnProperty.call(next, "posthog_experiment")) {
    next.posthog_experiment = PRICING_EXPERIMENT_NAME;
  }

  if (!Object.prototype.hasOwnProperty.call(next, "posthog_experiment_variant")) {
    next.posthog_experiment_variant = getPricingExperimentVariant();
  }

  if (!Object.prototype.hasOwnProperty.call(next, "posthog_nudge_variant")) {
    const parent = next.posthog_experiment_variant;
    next.posthog_nudge_variant =
      parent === "all-trials" ? getAnnualNudgeVariant() : null;
  }

  if (!Object.prototype.hasOwnProperty.call(next, "posthog_distinct_id")) {
    next.posthog_distinct_id = getPostHogDistinctId();
  }

  return next;
};

export const getAccountSubscriptions = async (id) => {
  return await subscriptionApi.list(id);
};

export const getStationSubscriptions = async (id) => {
  return await subscriptionApi.list(id, "stations");
};

export const getStationSubscription = async (id, priceId) => {
  return await subscriptionApi.get(id, priceId, "stations");
};

export const cancelStationSubscription = async (id, priceId) => {
  return await subscriptionApi.cancel(id, priceId, "stations");
};

export const cancelAccountSubscription = async (id, priceId) => {
  return await subscriptionApi.cancel(id, priceId);
};

export const createAccountSubscription = async (id, data) => {
  return await subscriptionApi.create(id, withPosthogExperimentProps(data));
};

export const getAccountPlans = async (id, useCurrencyOptions = false) => {
  return await subscriptionApi.getAccountPlans(id, useCurrencyOptions);
};

export const getAccountPlanFeatures = async (id) => {
  return await subscriptionApi.getAccountPlansFeatures(id);
};

// Resolves to { updateAllowed, noPaymentMethod }. A missing payment method gets no toast: the caller
// offers a way to add one instead. Every other refusal is explained in a toast here.
export const checkCanUpdateSubscription = async (accountId, priceId, quantity, promoCode) => {
  const result = await subscriptionApi.canUpdateSubscription(
    accountId,
    priceId,
    quantity,
    promoCode
  );

  const noPaymentMethod = !result.updateAllowed && !!result.reason?.noPaymentMethod;

  if (!result.updateAllowed && !noPaymentMethod) {
    let message = "Unknown error occurred";

    if (result.reason?.stationsCountExceeded) {
      message = i18n.t("subscriptions.stations_count_exceeded");
    } else if (result.reason?.podcastsCountExceeded) {
      message = i18n.t("subscriptions.podcasts_count_exceeded");
    } else if (result.reason?.maxTracksExceeded) {
      message = i18n.t("subscriptions.max_tracks_exceeded");
    } else if (result.reason?.maxBitRateExceeded) {
      message = i18n.t("subscriptions.max_bit_rate_exceeded");
    } else if (result.reason?.invalidQuantity) {
      message = i18n.t("subscriptions.invalid_quantity");
    } else if (result.reason?.invalidPromoCode) {
      message = i18n.t("subscriptions.invalid_promo_code");
    }

    await notificationError(
      message,
      i18n.t("subscriptions.plan_update_error"),
      12000
    );
  }

  return { updateAllowed: !!result.updateAllowed, noPaymentMethod };
};

export const addAccountSubscriptionPromoCode = async (accountId, subscriptionId, promoCode) => {
  return await subscriptionApi.addAccountSubscriptionPromoCode(
    accountId,
    subscriptionId,
    promoCode
  );
};

export const createAccountSubscriptionMultiItem = async (accountId, payload) => {
  return await subscriptionApi.createAccountSubscriptionMultiItem(
    accountId,
    withPosthogExperimentProps(payload)
  );
};

export const getCheckoutSessionStatus = async (accountId, sessionId) => {
  return await subscriptionApi.getCheckoutSessionStatus(accountId, sessionId);
};

export const previewAccountSubscriptionUpdate = async (accountId, params) => {
  return await subscriptionApi.previewSubscriptionUpdate(accountId, params);
};

export const updateAccountSubscriptionMultiItem = async (accountId, payload) => {
  return await subscriptionApi.updateAccountSubscriptionMultiItem(accountId, payload);
};

export default {
  getAccountSubscriptions,
  getStationSubscriptions,
  getStationSubscription,
  cancelStationSubscription,
  cancelAccountSubscription,
  createAccountSubscription,
  getAccountPlans,
  getAccountPlanFeatures,
  checkCanUpdateSubscription,
  addAccountSubscriptionPromoCode,
  createAccountSubscriptionMultiItem,
  getCheckoutSessionStatus,
  previewAccountSubscriptionUpdate,
  updateAccountSubscriptionMultiItem,
};
